// app/javascript/controllers/reconciliation_controller.js

import {Controller} from "stimulus"

export default class extends Controller {
    static targets = [
        "bankTransactionsTable",
        "selectedBankTransactionsTable",
        "glEntriesTable",
        "selectedGlEntriesTable",
        "bankTransactionsState",
        "glEntriesState",
        "reconcileButton",
        "maxAmount"
    ]

    initialize() {
        this.selectedBankTransactions = []
        this.selectedGlEntries = []
        this.selectedBankTransactionsTableTarget.addEventListener('click', (e) => {
            if (e.target.closest('tr')) {
                this.deselectBankTransaction(e.target.closest('tr'))
            }
        })

        this.selectedGlEntriesTableTarget.addEventListener('click', (e) => {
            if (e.target.closest('tr')) {
                this.deselectGlEntry(e.target.closest('tr'))
            }
        })

        // REMOVED: Don't add the event listener here
        // document.addEventListener('gl-entry-hidden', this.handleGlEntryHidden.bind(this))

        document.addEventListener('DOMContentLoaded', this.initializeAdjustmentForm.bind(this))
        const toggleButton = document.querySelector('.toggle-balance-adjustment-form')
        if (toggleButton) {
            toggleButton.addEventListener('click', this.toggleAdjustmentForm.bind(this))
        }
    }

    // Add this method to set up the custom event listener when the controller connects
    connect() {
        // Set up the custom event listener for gl-entry-hidden events
        this.boundHandleGlEntryHidden = this.handleGlEntryHidden.bind(this);
        document.addEventListener('gl-entry-hidden', this.boundHandleGlEntryHidden);

        // Apply initial padding to GL entries and bank transactions
        this.updateGlEntriesPadding()
        this.updateBankTransactionsPadding()
    }

    // Add this method to clean up when the controller disconnects
    disconnect() {
        // Remove the event listener to prevent memory leaks
        if (this.boundHandleGlEntryHidden) {
            document.removeEventListener('gl-entry-hidden', this.boundHandleGlEntryHidden);
        }
    }

    // Your existing method - but make sure it's handling the right event
    handleGlEntryHidden(event) {
        // Check if this is the custom event we expect and not a form submission
        if (!event.detail || event.type !== 'gl-entry-hidden') {
            return;
        }

        // Get the GL entry ID from the event details
        const { glEntryId, glEntryRowId } = event.detail;

        // Find the entry in the selected GL entries by matching the GL entry ID
        const index = this.selectedGlEntries.findIndex(item => {
            // Get the GL entry ID from the hidden input field
            const hiddenInput = item.originalRow.querySelector('input[name="gl_entry_ids[]"]');
            const matches = hiddenInput && hiddenInput.value === glEntryId;
            return matches;
        });

        if (index > -1) {
            // Remove the entry from the reconciliation workspace
            try {
                this.selectedGlEntries[index].clonedRow.remove()
            } catch (error) {
                // Error removing cloned row, it may have already been removed
            }
            // Remove from tracking array
            this.selectedGlEntries.splice(index, 1)

            // Update the state
            this.updateGlEntriesState()
            this.updateSelectedTransactionIds()
            this.checkReconcileButton()
        } else {
            // If we couldn't find the entry by the hidden input field, try to find it by the row ID
            const rowIndex = this.selectedGlEntries.findIndex(item => {
                const matches = item.originalRow.id === glEntryRowId;
                return matches;
            });

            if (rowIndex > -1) {
                // Remove the entry from the reconciliation workspace
                try {
                    this.selectedGlEntries[rowIndex].clonedRow.remove()
                } catch (error) {
                    // Error removing cloned row, it may have already been removed
                }
                // Remove from tracking array
                this.selectedGlEntries.splice(rowIndex, 1)

                // Update the state
                this.updateGlEntriesState()
                this.updateSelectedTransactionIds()
                this.checkReconcileButton()
            } else {
                // As a last resort, try to find any entries with stale DOM references and clean them up
                this.cleanupStaleEntries();
            }
        }
    }

    // Helper method to clean up entries with stale DOM references
    cleanupStaleEntries() {
        const validEntries = this.selectedGlEntries.filter(item => {
            // Check if the original row still exists in the DOM
            const stillInDOM = document.contains(item.originalRow);
            if (!stillInDOM) {
                try {
                    item.clonedRow.remove();
                } catch (error) {
                    // Error removing stale cloned row
                }
                return false; // Remove this entry from the array
            }
            return true; // Keep this entry
        });

        if (validEntries.length !== this.selectedGlEntries.length) {
            this.selectedGlEntries = validEntries;
            this.updateGlEntriesState();
            this.updateSelectedTransactionIds();
            this.checkReconcileButton();
        }
    }

    // ... rest of your methods remain the same ...

    selectBankTransaction(event) {
        const row = event.currentTarget

        // Check if already selected - if so, deselect it
        if (row.classList.contains("selected")) {
            this.deselectBankTransactionByOriginalRow(row)
            return
        }

        row.classList.add("selected")

        // Clone and add to selected table
        const clonedRow = row.cloneNode(true)
        this.selectedBankTransactionsTableTarget.appendChild(clonedRow)

        // Add to tracking array with both original row and cloned row references
        // Use data-transaction-amount attribute (clean numeric value) instead of parsing cell text
        const dataAmount = parseFloat(row.dataset.transactionAmount)
        this.selectedBankTransactions.push({
            originalRow: row,
            clonedRow: clonedRow,
            amount: isNaN(dataAmount) ? this.parseAmountFromText(row.cells[2].textContent) : dataAmount
        })

        this.updateBankTransactionsState()
        this.updateSelectedTransactionIds()
        this.checkReconcileButton()
        this.highlightMatchingGlEntries()
    }

    preventRowSelect(event) {
        event.stopPropagation(); // Prevent the row click from firing
    }

    selectGlEntry(event) {
        const row = event.currentTarget

        // Don't select hidden entries (they have darker background for visibility only)
        if (row.dataset.glEntryHidden === 'true') {
            return; // Exit early for hidden entries
        }

        // Check if already selected - if so, deselect it
        if (row.classList.contains("selected")) {
            this.deselectGlEntryByOriginalRow(row)
            return
        }

        // Additional check: see if this entry is already in our tracking array
        // This prevents duplicates when DOM is refreshed but tracking array still has the entry
        const glEntryId = row.querySelector('input[name="gl_entry_ids[]"]')?.value;
        const existingIndex = this.selectedGlEntries.findIndex(item => {
            const existingGlEntryId = item.originalRow.querySelector('input[name="gl_entry_ids[]"]')?.value;
            return existingGlEntryId === glEntryId;
        });

        if (existingIndex > -1) {
            // Remove the existing entry from the workspace
            this.selectedGlEntries[existingIndex].clonedRow.remove();
            this.selectedGlEntries.splice(existingIndex, 1);
            this.updateGlEntriesState();
            this.updateSelectedTransactionIds();
            this.checkReconcileButton();
            return;
        }
        row.classList.add("selected")

        // Clone and add to selected table
        const clonedRow = row.cloneNode(true)

        // Remove the "hide entry" column (actions column) from the cloned row
        // The actions column is the last column (index 3)
        if (clonedRow.cells.length > 3) {
            clonedRow.deleteCell(3); // Remove the actions cell
        }

        // Truncate description in the cloned row (for reconciliation workspace only)
        const descriptionCell = clonedRow.cells[1]; // Description is in the second column
        if (descriptionCell && descriptionCell.textContent.length > 30) {
            const fullDescription = descriptionCell.textContent;
            descriptionCell.textContent = descriptionCell.textContent.substring(0, 30) + '...';
            descriptionCell.title = fullDescription; // Add title attribute for hover text
        }

        this.selectedGlEntriesTableTarget.appendChild(clonedRow)

        // Add to tracking array with both original row and cloned row references
        this.selectedGlEntries.push({
            originalRow: row,
            clonedRow: clonedRow,
            amount: this.parseAmountFromText(row.cells[2].textContent)
        })

        this.updateGlEntriesState()
        this.updateSelectedTransactionIds()
        this.checkReconcileButton()
    }

    // New method to deselect by original row (when clicked in main list)
    deselectBankTransactionByOriginalRow(originalRow) {
        const index = this.selectedBankTransactions.findIndex(
            item => item.originalRow === originalRow
        )
        if (index > -1) {
            // Remove selected styling from original row
            originalRow.classList.remove("selected")
            // Remove cloned row from selected table
            this.selectedBankTransactions[index].clonedRow.remove()
            // Remove from tracking array
            this.selectedBankTransactions.splice(index, 1)

            this.updateBankTransactionsState()
            this.updateSelectedTransactionIds()
            this.checkReconcileButton()
            this.highlightMatchingGlEntries()
        }
    }

    // New method to deselect by original row (when clicked in main list)
    deselectGlEntryByOriginalRow(originalRow) {
        const index = this.selectedGlEntries.findIndex(
            item => item.originalRow === originalRow
        )
        if (index > -1) {
            // Remove selected styling from original row
            originalRow.classList.remove("selected")
            // Remove cloned row from selected table
            this.selectedGlEntries[index].clonedRow.remove()
            // Remove from tracking array
            this.selectedGlEntries.splice(index, 1)

            this.updateGlEntriesState()
            this.updateSelectedTransactionIds()
            this.checkReconcileButton()
        }
    }

    // Method for deselecting from the reconciliation workspace (when clicked on cloned row)
    deselectBankTransaction(clickedRow) {
        const index = this.selectedBankTransactions.findIndex(
            item => item.clonedRow === clickedRow
        )
        if (index > -1) {
            // Remove selected styling from original row
            this.selectedBankTransactions[index].originalRow.classList.remove("selected")
            // Remove cloned row from selected table
            clickedRow.remove()
            // Remove from tracking array
            this.selectedBankTransactions.splice(index, 1)

            this.updateBankTransactionsState()
            this.updateSelectedTransactionIds()
            this.checkReconcileButton()
            this.highlightMatchingGlEntries()
        }
    }

    // Method for deselecting from the reconciliation workspace (when clicked on cloned row)
    deselectGlEntry(clickedRow) {
        const index = this.selectedGlEntries.findIndex(
            item => item.clonedRow === clickedRow
        )
        if (index > -1) {
            // Remove selected styling from original row
            this.selectedGlEntries[index].originalRow.classList.remove("selected")
            // Remove cloned row from selected table
            clickedRow.remove()
            // Remove from tracking array
            this.selectedGlEntries.splice(index, 1)

            this.updateGlEntriesState()
            this.updateSelectedTransactionIds()
            this.checkReconcileButton()
        }
    }

    updateBankTransactionsState() {
        const total = this.selectedBankTransactions.reduce((sum, item) => sum + item.amount, 0)
        this.bankTransactionsStateTarget.textContent =
            `${this.selectedBankTransactions.length} entries, $${total.toFixed(2)} total`

        // Update padding based on number of transactions
        this.updateBankTransactionsPadding()
    }

    updateGlEntriesState() {
        const total = this.selectedGlEntries.reduce((sum, item) => sum + item.amount, 0)
        this.glEntriesStateTarget.textContent =
            `${this.selectedGlEntries.length} entries, $${total.toFixed(2)} total`

        // Update padding based on number of entries
        this.updateGlEntriesPadding()
    }

    updateGlEntriesPadding() {
        // Calculate padding: 20px base, reduced by 2px for each entry beyond 4
        const entryCount = this.selectedGlEntries.length
        let padding = 20 // Base padding

        if (entryCount > 4) {
            // Reduce padding by 2px for each entry beyond 4, with a minimum of 4px
            padding = Math.max(4, padding - ((entryCount - 4) * 2))
        }

        // Apply padding to all rows in the selected GL entries table
        const rows = this.selectedGlEntriesTableTarget.querySelectorAll('tr')
        rows.forEach(row => {
            const cells = row.querySelectorAll('td')
            cells.forEach(cell => {
                cell.style.paddingTop = `${padding}px`
                cell.style.paddingBottom = `${padding}px`
            })
        })
    }

    updateBankTransactionsPadding() {
        // Calculate padding: 20px base, reduced by 2px for each transaction beyond 4
        const transactionCount = this.selectedBankTransactions.length
        let padding = 20 // Base padding

        if (transactionCount > 4) {
            // Reduce padding by 2px for each transaction beyond 4, with a minimum of 4px
            padding = Math.max(4, padding - ((transactionCount - 4) * 2))
        }

        // Apply padding to all rows in the selected bank transactions table
        const rows = this.selectedBankTransactionsTableTarget.querySelectorAll('tr')
        rows.forEach(row => {
            const cells = row.querySelectorAll('td')
            cells.forEach(cell => {
                cell.style.paddingTop = `${padding}px`
                cell.style.paddingBottom = `${padding}px`
            })
        })
    }

    checkReconcileButton() {
        const bankTotal = this.selectedBankTransactions.reduce((sum, item) => sum + item.amount, 0);
        const glTotal = this.selectedGlEntries.reduce((sum, item) => sum + item.amount, 0);
        const difference = glTotal - bankTotal;
        const hasSelections = this.selectedBankTransactions.length > 0 && this.selectedGlEntries.length > 0;
        const balancesEqual = Math.abs(difference) < 0.001; // Account for floating point precision

        // Enable reconcile button only if we have transactions selected AND balances match
        this.reconcileButtonTarget.disabled = !hasSelections || !balancesEqual;

        // Update the difference message next to the button
        let diffMessage = document.getElementById('balance-difference-message');
        if (!diffMessage) {
            // Create the message element if it doesn't exist
            diffMessage = document.createElement('span');
            diffMessage.id = 'balance-difference-message';
            diffMessage.className = 'ml-2 text-red-500';
            this.reconcileButtonTarget.parentNode.insertBefore(diffMessage, this.reconcileButtonTarget.nextSibling);
        }
        const diffSearch = document.getElementById('filter-to-difference');
        // Show difference message only when there's actually a difference
        if (this.selectedBankTransactions.length > 0 && !balancesEqual) {
            diffMessage.textContent = `Difference: ${difference.toFixed(2)}`;
            diffMessage.classList.remove('hidden');
            diffSearch.classList.remove('hidden');
        } else {
            diffMessage.classList.add('hidden');
            diffSearch.classList.add('hidden');
        }

        // Update form values if it's visible
        this.updateAdjustmentFormValues();

        // Update the toggle button visibility based on if there's a difference
        const toggleButton = document.querySelector('.toggle-balance-adjustment-form');
        if (toggleButton) {
            if (this.selectedBankTransactions.length > 0 && !balancesEqual) {
                toggleButton.classList.remove('hidden');
            } else {
                toggleButton.classList.add('hidden');
                // Hide the form if there's no difference
                const form = document.getElementById('balance-adjustment-form');
                if (form) {
                    form.classList.add('hidden');
                }
            }
        }
    }

    updateAdjustmentFormValues() {
        const table = document.querySelector("#selected-bank-transactions-table");
        const row = table?.querySelector('tr');
        if (row && row.children && row.children[1]) {
            const element = row.children[1];

            const form = document.getElementById('balance-adjustment-form');
            if (!form) return;

            const bankTotal = this.selectedBankTransactions.reduce((sum, item) => sum + item.amount, 0);
            const glTotal = this.selectedGlEntries.reduce((sum, item) => sum + item.amount, 0);
            const difference = bankTotal - glTotal;

            // Update displayed values
            document.getElementById('bank-transactions-total').textContent = bankTotal.toFixed(2);
            document.getElementById('gl-entries-total').textContent = glTotal.toFixed(2);
            document.getElementById('adjustment-amount').textContent = Math.abs(difference).toFixed(2);
            document.getElementById('adjustment-direction').textContent = difference > 0 ? "credit" : "debit";
            document.getElementById('gl-adjustment-amount').value = Math.abs(difference).toFixed(2);
            document.getElementById('bank-account-adjustment-amount').value = difference.toFixed(2);
            document.getElementById('description').value = "Reconciliation Adjustment: " +
            document.querySelector("#selected-bank-transactions-table").querySelector('tr').children[1].innerText;

            // Get first bank transaction date for the form
            if (this.selectedBankTransactions.length > 0) {
                const firstTransDate = this.selectedBankTransactions[0].originalRow.dataset.transactionDate;
                document.getElementById('gl-adjustment-date').value = firstTransDate;
            }
        }
    }

    toggleAdjustmentForm() {
        const form = document.getElementById('balance-adjustment-form')
        if (form) {
            form.classList.toggle('hidden')
            this.updateAdjustmentFormValues()
        }
    }

    initializeAdjustmentForm() {
        // If the balance adjustment form exists, set up the cancel button
        const balanceForm = document.getElementById('balance-adjustment-form')
        if (balanceForm) {
            const cancelButton = balanceForm.querySelector('button[type=button]')
            if (cancelButton) {
                cancelButton.addEventListener('click', () => {
                    balanceForm.classList.add('hidden')
                })
            }

        }

        // Update form when bank transactions are selected
        this.updateSelectedTransactionIds()
    }

    // Add method to populate hidden fields with selected bank transaction IDs
    updateSelectedTransactionIds() {
        // Handle bank transaction IDs
        let bankContainer = document.getElementById('selected-bank-transaction-ids-container');
        if (bankContainer) {
            bankContainer.innerHTML = '';

            // Add all selected bank transaction IDs as hidden fields
            if (this.selectedBankTransactions) {
                this.selectedBankTransactions.forEach(item => {
                    const idInput = item.originalRow.querySelector('input[type=hidden]');
                    if (idInput) {
                        const clone = idInput.cloneNode(true);
                        bankContainer.appendChild(clone);
                    }
                });
            }
        }

        // Handle GL entry IDs
        let glContainer = document.getElementById('selected-gl-entry-ids-container');
        if (glContainer) {
            glContainer.innerHTML = '';

            // Add all selected GL entry IDs as hidden fields
            if (this.selectedGlEntries) {
                this.selectedGlEntries.forEach(item => {
                    const idInput = item.originalRow.querySelector('input[type=hidden]');
                    if (idInput) {
                        const clone = idInput.cloneNode(true);
                        // Rename the field to gl_entry_ids[] to match controller expectations
                        clone.name = 'gl_entry_ids[]';
                        glContainer.appendChild(clone);
                    }
                });
            }
        }
    }

    autoFillMax(event) {
        const minAmount = parseFloat(event.target.value);
        const maxAmountField = this.maxAmountTarget;

        if (!maxAmountField.value) {
            maxAmountField.value = minAmount;
        }
    }

    searchForDifference(event) {
        event.preventDefault();

        // Calculate the current difference
        const bankTotal = this.selectedBankTransactions.reduce((sum, item) => sum + item.amount, 0);
        const glTotal = this.selectedGlEntries.reduce((sum, item) => sum + item.amount, 0);
        const difference = bankTotal - glTotal;

        // Get the earliest date from the selected bank transactions
        let earliestDate = null;
        if (this.selectedBankTransactions.length > 0) {
            earliestDate = this.selectedBankTransactions
                .map(item => item.originalRow.dataset.transactionDate)
                .sort()[0]; // Sort dates and take the first one
        } else {
            // If no transactions selected, use the balance period start date
            const startDateField = document.querySelector('input[name="start_date"]');
            if (startDateField) {
                earliestDate = startDateField.value;
            }
        }

        if (earliestDate) {
            // Calculate dates 30 days before and 5 days after
            const startDate = new Date(earliestDate);
            startDate.setDate(startDate.getDate() - 30);

            const endDate = new Date(earliestDate);
            endDate.setDate(endDate.getDate() + 5);

            // Format dates as YYYY-MM-DD
            const formatDate = (date) => {
                return date.toISOString().split('T')[0];
            };

            // Find and update the filter form fields
            const startDateField = document.querySelector('input[name="start_date"]');
            const endDateField = document.querySelector('input[name="end_date"]');
            const amountField = document.querySelector('input[name="min_amount"]');
            const filterButton = document.querySelector('.date-range-filter form input[type="submit"]');

            if (startDateField && endDateField && amountField && filterButton) {
                startDateField.value = formatDate(startDate);
                endDateField.value = formatDate(endDate);
                amountField.value = difference.toFixed(2);

                // Also set max amount to the same value
                const maxAmountField = this.maxAmountTarget;
                if (maxAmountField) {
                    maxAmountField.value = difference.toFixed(2);
                }

                // Submit the form to apply the filter
                filterButton.click();
            }
        }
    }

    // Helper method to parse amount from currency-formatted text
    parseAmountFromText(text) {
        if (!text) return 0;

        // Remove currency symbols, commas, and whitespace, keeping only digits, decimal point, and minus sign
        const cleanedText = text.toString().replace(/[$,\s]/g, '');

        // Parse the cleaned text as a float
        const amount = parseFloat(cleanedText);

        // Return 0 if parsing failed (NaN), otherwise return the parsed amount
        return isNaN(amount) ? 0 : amount;
    }

    // Auto-matching: highlight GL entries that match selected bank transactions
    highlightMatchingGlEntries() {
        this.clearGlEntryHighlights()

        if (this.selectedBankTransactions.length === 0) return

        // Collect all match IDs from the selected bank transactions' data attributes
        const allMatchIds = new Set()
        this.selectedBankTransactions.forEach(item => {
            const matchAttr = item.originalRow.dataset.matchGlEntryIds
            if (matchAttr) {
                matchAttr.split(',').filter(id => id).forEach(id => allMatchIds.add(id))
            }
        })

        if (allMatchIds.size === 0) {
            // No pre-computed matches — try server-side suggestion
            this.fetchServerSuggestions()
            return
        }

        // Highlight matching GL entry rows in the table
        const glTable = this.glEntriesTableTarget
        if (!glTable) return

        const rows = glTable.querySelectorAll('tr[data-gl-entry-id]')
        rows.forEach(row => {
            if (allMatchIds.has(row.dataset.glEntryId)) {
                row.classList.add('auto-match-highlight')
            }
        })
    }

    clearGlEntryHighlights() {
        const glTable = this.glEntriesTableTarget
        if (!glTable) return

        glTable.querySelectorAll('tr.auto-match-highlight').forEach(row => {
            row.classList.remove('auto-match-highlight')
        })

        // Clear any previous GL account and description suggestions
        this.clearSuggestions()
    }

    clearSuggestions() {
        this._suggestedAccount = null
        this._suggestedDescription = null
        this._suggestions = null

        const accountContainer = document.getElementById('gl-account-suggestion-container')
        if (accountContainer) accountContainer.remove()

        const descriptionContainer = document.getElementById('description-suggestion-container')
        if (descriptionContainer) descriptionContainer.remove()
    }

    // Fetch server-side match suggestions when no pre-computed matches exist
    fetchServerSuggestions() {
        if (this.selectedBankTransactions.length === 0) return

        const firstSelected = this.selectedBankTransactions[0]
        const suggestUrl = firstSelected.originalRow.dataset.suggestUrl
        if (!suggestUrl) return

        // Cancel any in-flight request before starting a new one
        if (this._suggestionsAbortController) {
            this._suggestionsAbortController.abort()
        }

        const abortController = new AbortController()
        this._suggestionsAbortController = abortController

        // Capture the bank transaction ID at request time to detect stale responses
        const requestedTransactionId = firstSelected.originalRow.dataset.bankTransactionId

        fetch(suggestUrl, {
            signal: abortController.signal,
            headers: {
                'Accept': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            }
        })
        .then(response => response.json())
        .then(data => {
            // Ignore stale responses if the selection has changed
            const currentFirst = this.selectedBankTransactions[0]
            if (!currentFirst || currentFirst.originalRow.dataset.bankTransactionId !== requestedTransactionId) {
                return
            }

            if (data.matching_gl_entry_ids && data.matching_gl_entry_ids.length > 0) {
                // Persist the returned IDs on the bank transaction row so we don't re-fetch
                firstSelected.originalRow.dataset.matchGlEntryIds = data.matching_gl_entry_ids.join(',')

                // Auto-trigger the search to widen the GL entries filter
                this.autoSearchForMatches()
            } else if (data.suggestions && data.suggestions.length > 0) {
                this.applySuggestions(data.suggestions)
            } else if (data.suggested_gl_account) {
                this.applySuggestions([{
                    gl_account: data.suggested_gl_account,
                    description: data.suggested_description,
                    reason: data.suggestion_reason
                }])
            }
        })
        .catch((error) => {
            // Ignore abort errors; silently handle other fetch errors
            if (error.name === 'AbortError') return
        })
        .finally(() => {
            if (this._suggestionsAbortController === abortController) {
                this._suggestionsAbortController = null
            }
        })
    }

    // Auto-trigger the search filter (like the magnifying glass) for exact amount match
    autoSearchForMatches() {
        if (this.selectedBankTransactions.length === 0) return

        const firstSelected = this.selectedBankTransactions[0]
        const amount = firstSelected.amount
        const transactionDate = firstSelected.originalRow.dataset.transactionDate

        if (!transactionDate) return

        const startDate = new Date(transactionDate)
        startDate.setDate(startDate.getDate() - 30)

        const endDate = new Date(transactionDate)
        endDate.setDate(endDate.getDate() + 5)

        const formatDate = (date) => date.toISOString().split('T')[0]

        const startDateField = document.querySelector('input[name="start_date"]')
        const endDateField = document.querySelector('input[name="end_date"]')
        const amountField = document.querySelector('input[name="min_amount"]')
        const filterButton = document.querySelector('.date-range-filter form input[type="submit"]')

        if (startDateField && endDateField && amountField && filterButton) {
            startDateField.value = formatDate(startDate)
            endDateField.value = formatDate(endDate)
            amountField.value = amount.toFixed(2)

            const maxAmountField = this.maxAmountTarget
            if (maxAmountField) {
                maxAmountField.value = amount.toFixed(2)
            }

            // Submit the filter form, which will cause Turbo to re-render the GL entries frame
            filterButton.click()

            // After the GL entries frame reloads, re-apply highlights to the matching rows
            const glEntriesFrame = document.getElementById('gl-entries-frame')
            if (glEntriesFrame) {
                const handleFrameLoad = (event) => {
                    if (event.target === glEntriesFrame) {
                        document.removeEventListener('turbo:frame-load', handleFrameLoad)
                        this.highlightMatchingGlEntries()
                    }
                }
                document.addEventListener('turbo:frame-load', handleFrameLoad)
            }
        }
    }

    // Show clickable GL account suggestions below the expense account dropdown
    applySuggestions(suggestions) {
        this._suggestions = suggestions
        this._suggestedAccount = suggestions[0].gl_account
        this.showAccountSuggestions(suggestions)

        // Store first suggested description for overwrite/append
        if (suggestions[0].description) {
            this._suggestedDescription = suggestions[0].description
            this.showDescriptionSuggestion(suggestions[0].description)
        }
    }

    // Display clickable suggestions below the expense account dropdown
    showAccountSuggestions(suggestions) {
        let container = document.getElementById('gl-account-suggestion-container')
        if (!container) {
            container = document.createElement('div')
            container.id = 'gl-account-suggestion-container'
            container.className = 'mt-1 space-y-1'
            const selectField = document.querySelector('#offset_gl_account_id, select[name="offset_gl_account_id"]')
            if (selectField) {
                selectField.closest('.form-group').appendChild(container)
            }
        }

        const items = suggestions.map((s, index) => {
            const reasonText = s.reason ? `<span class="text-gray-500 text-xs block">${this.escapeHtml(s.reason)}</span>` : ''
            return `
                <a href="#" class="block p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800 hover:bg-blue-100 transition-colors"
                   data-action="click->reconciliation#applyAccountSuggestion"
                   data-suggestion-index="${index}">
                    <span class="font-medium">${this.escapeHtml(s.gl_account.name)}</span>
                    ${reasonText}
                </a>
            `
        }).join('')

        container.innerHTML = items
    }

    // Apply the suggested GL account when a suggestion is clicked
    applyAccountSuggestion(event) {
        event.preventDefault()
        if (!this._suggestions) return

        const index = parseInt(event.currentTarget.dataset.suggestionIndex || '0', 10)
        const suggestion = this._suggestions[index]
        if (!suggestion) return

        const account = suggestion.gl_account
        const selectField = document.querySelector('#offset_gl_account_id, select[name="offset_gl_account_id"]')
        if (!selectField) return

        const option = selectField.querySelector(`option[value="${account.id}"]`)
        if (option) {
            selectField.value = account.id

            if (selectField.slim) {
                selectField.slim.set(account.id.toString())
            }

            selectField.dispatchEvent(new Event('change', { bubbles: true }))
        }

        // Update description suggestion if this suggestion has one
        if (suggestion.description) {
            this._suggestedDescription = suggestion.description
            this.showDescriptionSuggestion(suggestion.description)
        }

        // Remove the suggestions after applying
        const container = document.getElementById('gl-account-suggestion-container')
        if (container) container.remove()

        this.validateAdjustmentForm()
    }

    // Show description suggestion with overwrite/append options inline
    showDescriptionSuggestion(suggestedDescription) {
        let container = document.getElementById('description-suggestion-container')
        if (!container) {
            container = document.createElement('div')
            container.id = 'description-suggestion-container'
            container.className = 'mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm flex items-center gap-2 flex-wrap'
            const descriptionField = document.getElementById('description')
            if (descriptionField) {
                descriptionField.parentElement.appendChild(container)
            }
        }

        container.innerHTML = `
            <span class="text-gray-700"><strong>Suggested:</strong> ${this.escapeHtml(suggestedDescription)}</span>
            <button type="button" class="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 whitespace-nowrap"
                    data-action="click->reconciliation#overwriteDescription">
                Use suggested
            </button>
            <button type="button" class="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 whitespace-nowrap"
                    data-action="click->reconciliation#appendDescription">
                Append to current
            </button>
        `
    }

    // Replace the description field with the suggested description
    overwriteDescription(event) {
        event.preventDefault()
        const descriptionField = document.getElementById('description')
        if (descriptionField && this._suggestedDescription) {
            descriptionField.value = this._suggestedDescription
        }
    }

    // Append the suggested description to the current description
    appendDescription(event) {
        event.preventDefault()
        const descriptionField = document.getElementById('description')
        if (descriptionField && this._suggestedDescription) {
            const current = descriptionField.value.trim()
            descriptionField.value = current ? `${current} — ${this._suggestedDescription}` : this._suggestedDescription
        }
    }

    // Intercept form submission and validate required GL account fields client-side.
    // Prevents the server-side redirect that would lose selected transactions.
    validateAdjustmentSubmit(event) {
        const errors = this._collectAdjustmentErrors()
        if (errors.length > 0) {
            event.preventDefault()
            this._showAdjustmentErrors(errors)
        }
    }

    // Enable or disable the Create Adjustment & Reconcile button based on required fields
    validateAdjustmentForm() {
        const submitButton = document.getElementById('create-adjustment-button')
        if (!submitButton) return

        const errors = this._collectAdjustmentErrors()
        submitButton.disabled = errors.length > 0

        // Update hint text below the button
        this._updateValidationHint(errors)

        // Clear any previously shown inline errors when fields become valid
        if (errors.length === 0) {
            this._clearAdjustmentErrors()
        }
    }

    // Return an array of { field, message } objects for missing required GL accounts
    _collectAdjustmentErrors() {
        const errors = []
        const expenseSelect = document.querySelector('select[name="offset_gl_account_id"]')
        const assetSelect = document.querySelector('select[name="bank_gl_account_id"]')
        const assetHidden = document.querySelector('input[type="hidden"][name="bank_gl_account_id"]')

        if (!assetHidden && (!assetSelect || assetSelect.value === '')) {
            errors.push({ field: 'bank_gl_account_id', message: 'Please select an asset account.' })
        }

        if (!expenseSelect || expenseSelect.value === '') {
            errors.push({ field: 'offset_gl_account_id', message: 'Please select an expense account.' })
        }

        return errors
    }

    // Display inline error messages below the relevant dropdowns
    _showAdjustmentErrors(errors) {
        this._clearAdjustmentErrors()

        errors.forEach(({ field, message }) => {
            const select = document.querySelector(`select[name="${field}"]`)
            if (!select) return

            const errorEl = document.createElement('p')
            errorEl.className = 'adjustment-field-error text-rose-600 text-sm mt-1'
            errorEl.textContent = message
            select.closest('.form-group').appendChild(errorEl)
        })

        this._updateValidationHint(errors)
    }

    // Remove all inline adjustment error messages
    _clearAdjustmentErrors() {
        document.querySelectorAll('.adjustment-field-error').forEach(el => el.remove())
    }

    // Show or hide the validation hint below the submit button
    _updateValidationHint(errors) {
        const hint = document.getElementById('adjustment-validation-hint')
        if (!hint) return

        if (errors.length > 0) {
            hint.textContent = errors.map(e => e.message).join('. ')
            hint.classList.remove('hidden')
        } else {
            hint.classList.add('hidden')
        }
    }

    // Escape HTML to prevent XSS in dynamic content
    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }
}
